import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import { debounce, get, sortBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldSetWrapper } from '~/components/GenericForm/FieldSetWrapper';
import { CostCenter } from '~/data/costCenters';
import { EmpPosition } from '~/data/employee';
import { CostCentersListComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/useStyles';
import { ScheduleEmploymentFormCostCenterDetails } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useVisible } from '~/hooks/useVisible';

type Props = {
  getCostCenterValues: (positionUnit?: number) => CostCenter[];
  index: number;
  empPositionPending?: EmpPosition;
};

export const CostCenterComponent = (props: Props) => {
  const { getCostCenterValues, index, empPositionPending } = props;

  const sxStyles = useSxStyles();

  const { t } = useTranslation();
  const requiredText = t('fieldIsRequired');

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<Form>();

  const hasCollapsedContentError = !!get(
    errors,
    `schedule.${index}.empPosition.totalCostCenterPercentage`,
  );

  const { isVisible, toggle } = useVisible();

  useWatch({
    control,
    name: `schedule.${index}.empPosition.fInternalPositionUnit`,
  });
  const fInternalPositionUnitValue = getValues(
    `schedule.${index}.empPosition.fInternalPositionUnit`,
  );

  const selectedCostCenter = useWatch({
    control,
    name: `schedule.${index}.empPosition.fEmpCostCenter`,
  });

  const costCenterOptions = getCostCenterValues(fInternalPositionUnitValue);
  const findCostCenterValue = useCallback(
    (costCenterId: number, unitId?: number) =>
      getCostCenterValues(unitId).find(
        (cc) => cc.fCostCenterId === costCenterId,
      ),
    [getCostCenterValues],
  );

  const { isCostCenterSelected } = useMemo(() => {
    const inValidCostCenterValue =
      !selectedCostCenter ||
      selectedCostCenter === -1 ||
      !findCostCenterValue(selectedCostCenter);

    return {
      isCostCenterSelected: !inValidCostCenterValue,
    };
  }, [selectedCostCenter, findCostCenterValue]);

  const onCostCenterDetailsSync = useMemo(
    () =>
      debounce((oldCostCenterId: number, newCostCenterId: number) => {
        const costCenterDetails = getValues(
          `schedule.${index}.empPosition.costCenterDetails`,
        );

        const resolveUpdatedDetails =
          (): ScheduleEmploymentFormCostCenterDetails[] => {
            const isNewAlreadyListed = costCenterDetails.some(
              (cc) => cc.costCenterId === newCostCenterId,
            );

            if (isNewAlreadyListed) {
              // If both CostCenters are listed, just change the default
              return sortBy(
                costCenterDetails.map(
                  (cc): ScheduleEmploymentFormCostCenterDetails => {
                    return {
                      ...cc,
                      isDefault: cc.costCenterId === newCostCenterId,
                    };
                  },
                ),
                ({ isDefault }) => (isDefault ? 0 : 1),
              );
            } else {
              const newCostCenter: ScheduleEmploymentFormCostCenterDetails = {
                costCenterId: newCostCenterId,
                isDefault: true,
                customData: {},
              };

              const rest = costCenterDetails.filter(
                (cc) => cc.costCenterId !== oldCostCenterId,
              );

              return [newCostCenter, ...rest];
            }
          };

        setValue(
          `schedule.${index}.empPosition.costCenterDetails`,
          resolveUpdatedDetails(),
        );
      }, 600),
    [index, getValues, setValue],
  );

  const isCostCentersListLockedVisible = useCallback(() => {
    const costCenterDetails = getValues(
      `schedule.${index}.empPosition.costCenterDetails`,
    );

    const hasAdditionalCostCenters = costCenterDetails.length > 1;

    if (!hasAdditionalCostCenters) {
      const firstCostCenter = costCenterDetails[0];

      if (!!firstCostCenter) {
        const { percent, customData } = firstCostCenter;
        const hasCustomData: boolean = !!customData
          ? Object.values(customData).some((ccValue) => !!ccValue)
          : false;

        return !!percent || hasCustomData;
      }
    }

    return hasAdditionalCostCenters;
  }, [index, getValues]);

  const [lockVisible, setLockVisible] = useState(
    isCostCentersListLockedVisible(),
  );

  const isCollapsedContentButtonDisabled = !isCostCenterSelected || lockVisible;
  const isCollapsedContentOpen =
    (isCostCenterSelected && isVisible) ||
    lockVisible ||
    hasCollapsedContentError;

  return (
    <FieldSetWrapper>
      <Grid container>
        <Grid item xs={12} sx={sxStyles.fieldAndButton}>
          <Box>
            <Controller
              control={control}
              name={`schedule.${index}.empPosition.fEmpCostCenter`}
              rules={{
                validate: (value) => (value && value !== -1) || requiredText,
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Autocomplete
                  placeholder={
                    fInternalPositionUnitValue === -1
                      ? t('laSelectFirstOrgUnit')
                      : ' '
                  }
                  options={costCenterOptions}
                  getOptionLabel={(c) =>
                    `${c.fCostCenterCode}, ${c.fCostCenterName}`
                  }
                  onChange={(e, newValue) => {
                    onChange(newValue?.fCostCenterId || -1);
                    if (newValue?.fCostCenterId) {
                      onCostCenterDetailsSync(value, newValue.fCostCenterId);
                    }
                  }}
                  value={costCenterOptions.find(
                    (cc) => cc.fCostCenterId === value,
                  )}
                  size={'small'}
                  data-testid="cost-center-autocomplete"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('fEmpCostCenter')}
                      required={true}
                      error={!!error}
                      helperText={error?.message}
                      data-testid="cost-center-input"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  fullWidth={true}
                  disableClearable={true}
                />
              )}
            />
          </Box>
          <Box>
            <Button
              onClick={toggle}
              disabled={isCollapsedContentButtonDisabled}
              endIcon={
                isCollapsedContentOpen ? <ArrowDropDown /> : <ArrowDropUp />
              }
              color={hasCollapsedContentError ? 'error' : 'primary'}
              data-testid="toggle-cost-center-percentage-button"
            >
              {t('laPercCostCenter')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Collapse
            in={isCollapsedContentOpen}
            unmountOnExit
            easing={'easing'}
            timeout={500}
          >
            <Divider sx={{ margin: '16px 0 0 0' }} />
            <CostCentersListComponent
              control={control}
              costCenterOptions={costCenterOptions}
              index={index}
              selectedCostCenterId={
                isCostCenterSelected ? selectedCostCenter : undefined
              }
              empPositionPending={empPositionPending}
              onListUpdate={() => {
                const newLockState = isCostCentersListLockedVisible();
                if (newLockState !== lockVisible) {
                  setLockVisible(newLockState);
                }
              }}
            />
          </Collapse>
        </Grid>
      </Grid>
    </FieldSetWrapper>
  );
};
